import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../../components/layout/DashboardLayout';
import api from '../../services/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  ArrowLeft,
  Save,
  Loader2,
  School,
  Settings,
  Key,
  Globe,
  CheckCircle,
  XCircle
} from 'lucide-react';

const SchoolConfigPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [school, setSchool] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    contact_person: '',
    contact_phone: '',
    contact_email: '',
    veracross_school_route: '',
    veracross_client_id: '',
    veracross_client_secret: ''
  });

  useEffect(() => {
    fetchSchool();
  }, [id]);

  const fetchSchool = async () => {
    try {
      const response = await api.get(`/schools/${id}`);
      setSchool(response.data);
      setFormData({
        name: response.data.name || '',
        address: response.data.address || '',
        contact_person: response.data.contact_person || '',
        contact_phone: response.data.contact_phone || '',
        contact_email: response.data.contact_email || '',
        veracross_school_route: response.data.veracross_school_route || '',
        veracross_client_id: '',
        veracross_client_secret: ''
      });
    } catch (error) {
      toast.error('Failed to fetch school');
      navigate('/admin/schools');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.contact_email) {
      toast.error('Please fill in school name and admin email');
      return;
    }

    setSaving(true);
    try {
      const updateData = {
        name: formData.name,
        address: formData.address,
        contact_person: formData.contact_person,
        contact_phone: formData.contact_phone,
        contact_email: formData.contact_email,
        veracross_school_route: formData.veracross_school_route
      };

      // Only include credentials if they were entered
      if (formData.veracross_client_id) {
        updateData.veracross_client_id = formData.veracross_client_id;
      }
      if (formData.veracross_client_secret) {
        updateData.veracross_client_secret = formData.veracross_client_secret;
      }

      await api.patch(`/schools/${id}`, updateData);
      toast.success('School configuration saved');
      setConnectionStatus(null); // Reset status after save
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleTestConnection = async () => {
    if (!formData.veracross_school_route) {
      toast.error('Please enter the Veracross school route first');
      return;
    }

    setTesting(true);
    setConnectionStatus(null);
    
    try {
      // First save the current config
      await handleSave();
      
      // Then test by trying to sync teachers (will fail gracefully if credentials wrong)
      const response = await api.post('/teachers/sync');
      setConnectionStatus('success');
      toast.success(response.data.message || 'Connection successful!');
    } catch (error) {
      setConnectionStatus('error');
      toast.error(error.response?.data?.detail || 'Connection failed - check your credentials');
    } finally {
      setTesting(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="school-config-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/schools')}>
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Schools
            </Button>
            <div>
              <h1 className="font-outfit text-2xl font-bold text-slate-900">{school?.name}</h1>
              <p className="text-slate-500">School Configuration</p>
            </div>
          </div>
          <Button 
            className="bg-indigo-600 hover:bg-indigo-700" 
            onClick={handleSave}
            disabled={saving}
            data-testid="save-school-config-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
            Save Configuration
          </Button>
        </div>

        {/* School Information */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit flex items-center gap-2">
              <School className="h-5 w-5" />
              School Information
            </CardTitle>
            <CardDescription>Basic school details and contact information</CardDescription>
          </CardHeader>
          <CardContent className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="name">School Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Speyer Academy"
                data-testid="school-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="address">Address</Label>
              <Input
                id="address"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="123 School St, City, ST"
                data-testid="school-address-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_person">Contact Person</Label>
              <Input
                id="contact_person"
                value={formData.contact_person}
                onChange={(e) => setFormData({ ...formData, contact_person: e.target.value })}
                placeholder="John Smith"
                data-testid="school-contact-person-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                placeholder="(555) 123-4567"
                data-testid="school-contact-phone-input"
              />
            </div>
            <div className="col-span-2 space-y-2">
              <Label htmlFor="contact_email">Admin Email *</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                placeholder="admin@school.edu"
                data-testid="school-contact-email-input"
              />
            </div>
          </CardContent>
        </Card>

        {/* Veracross Configuration */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="font-outfit flex items-center gap-2">
                  <Settings className="h-5 w-5" />
                  Veracross API Configuration
                </CardTitle>
                <CardDescription>
                  Connect to Veracross to sync teachers, departments, and rooms
                </CardDescription>
              </div>
              {connectionStatus && (
                <Badge variant={connectionStatus === 'success' ? 'default' : 'destructive'}>
                  {connectionStatus === 'success' ? (
                    <><CheckCircle className="h-3 w-3 mr-1" /> Connected</>
                  ) : (
                    <><XCircle className="h-3 w-3 mr-1" /> Connection Failed</>
                  )}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="p-4 bg-slate-50 rounded-lg border border-slate-200">
              <div className="flex items-start gap-3">
                <Globe className="h-5 w-5 text-indigo-600 mt-0.5" />
                <div>
                  <p className="font-medium text-slate-900">School Route</p>
                  <p className="text-sm text-slate-500 mb-3">
                    This is your Veracross subdomain. For example, if your Axiom URL is 
                    <code className="mx-1 px-1 bg-slate-200 rounded">https://axiom.veracross.com/speyer</code>, 
                    the school route is <code className="px-1 bg-slate-200 rounded">speyer</code>
                  </p>
                  <Input
                    value={formData.veracross_school_route}
                    onChange={(e) => setFormData({ ...formData, veracross_school_route: e.target.value })}
                    placeholder="speyer"
                    className="max-w-xs"
                    data-testid="veracross-route-input"
                  />
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="client_id" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Client ID *
                </Label>
                <Input
                  id="client_id"
                  value={formData.veracross_client_id}
                  onChange={(e) => setFormData({ ...formData, veracross_client_id: e.target.value })}
                  placeholder="Enter new Client ID (leave blank to keep existing)"
                  data-testid="veracross-client-id-input"
                />
                <p className="text-xs text-slate-500">Leave blank to keep existing credentials</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="client_secret" className="flex items-center gap-2">
                  <Key className="h-4 w-4" />
                  Client Secret *
                </Label>
                <Input
                  id="client_secret"
                  type="password"
                  value={formData.veracross_client_secret}
                  onChange={(e) => setFormData({ ...formData, veracross_client_secret: e.target.value })}
                  placeholder="Enter new Client Secret (leave blank to keep existing)"
                  data-testid="veracross-client-secret-input"
                />
                <p className="text-xs text-slate-500">Leave blank to keep existing credentials</p>
              </div>
            </div>

            <div className="pt-4 border-t">
              <Button
                variant="outline"
                onClick={handleTestConnection}
                disabled={testing || !formData.veracross_school_route}
                data-testid="test-connection-btn"
              >
                {testing ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                    Testing Connection...
                  </>
                ) : (
                  <>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Test Connection & Sync Teachers
                  </>
                )}
              </Button>
              <p className="text-xs text-slate-500 mt-2">
                This will save the configuration and attempt to sync teachers from Veracross
              </p>
            </div>
          </CardContent>
        </Card>

        {/* API Scopes Info */}
        <Card>
          <CardHeader>
            <CardTitle className="font-outfit text-base">Required API Scopes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-600 mb-3">
              Ensure your Veracross OAuth application has the following scopes enabled:
            </p>
            <div className="flex flex-wrap gap-2">
              <Badge variant="outline">staff_faculty:list</Badge>
              <Badge variant="outline">academics.departments:list</Badge>
              <Badge variant="outline">academics.rooms:list</Badge>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default SchoolConfigPage;
